import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";

import { API_URL } from "config";
import { useAuth } from "AuthContext";
import FlagLoader from "layouts/flagloader";

import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import VisibilityIcon from "@mui/icons-material/Visibility";

const emptyForm = {
  title: "",
  description: "",
  document: null,
};

const emptyAction = {
  open: false,
  type: "delete",
  policy: null,
  reason: "",
};

const headerCellSx = {
  backgroundColor: "#dff0fb",
  color: "#61728f",
  fontSize: "14px",
  fontWeight: 400,
  letterSpacing: "0.02em",
  textTransform: "none",
  borderBottom: "1px solid #d6e4ef",
  py: 1.6,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  color: "#2f456b",
  fontSize: "14px",
  fontWeight: 400,
  borderBottom: "1px solid #e8edf3",
  py: 1.6,
  verticalAlign: "top",
};

const actionButtonSx = {
  width: 34,
  height: 34,
  mx: 0.25,
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function HrPolicy({ isTrashed = false }) {
  const navigate = useNavigate();
  const { permissions: authPermissions = [] } = useAuth();

  const canViewPolicy = authPermissions.length === 0 || authPermissions.includes("ViewHRPolicy");
  const canAddPolicy = authPermissions.length === 0 || authPermissions.includes("AddHRPolicy");
  const canDeletePolicy = authPermissions.length === 0 || authPermissions.includes("DeleteHRPolicy");
  const canTrashedPolicy = authPermissions.length === 0 || authPermissions.includes("TrashedHRPolicy");
  const canRestorePolicy = authPermissions.length === 0 || authPermissions.includes("RestoreHRPolicy");

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState(emptyAction);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const token = localStorage.getItem("authToken");

  const showMessage = (message, color = "success") => {
    setSnackbar({ open: true, message, color });
  };

  const fetchPolicies = async () => {
    if (!isTrashed && !canViewPolicy) {
      setPolicies([]);
      setLoading(false);
      showMessage("You do not have permission to view HR policies", "error");
      return;
    }

    if (isTrashed && !canTrashedPolicy) {
      setPolicies([]);
      setLoading(false);
      showMessage("You do not have permission to view trashed HR policies", "error");
      return;
    }

    try {
      setLoading(true);
      const endpoint = isTrashed ? `${API_URL}/hr-policies/trashed/list` : `${API_URL}/hr-policies`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPolicies(response.data?.data || []);
    } catch (error) {
      showMessage(error.response?.data?.message || "Failed to load HR policies", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrashed, canViewPolicy, canTrashedPolicy]);

  const closeModal = (force = false) => {
    if (submitting && !force) return;
    setModalOpen(false);
    setForm(emptyForm);
  };

  const openActionDialog = (type, policy) => {
    setActionDialog({ open: true, type, policy, reason: "" });
  };

  const closeActionDialog = () => {
    if (submitting) return;
    setActionDialog(emptyAction);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      showMessage("Please enter policy title", "error");
      return;
    }

    if (!form.document) {
      showMessage("Please upload a policy document", "error");
      return;
    }

    const payload = new FormData();
    payload.append("title", form.title.trim());
    payload.append("description", form.description.trim());
    payload.append("document", form.document);

    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/hr-policies`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      showMessage("Policy uploaded successfully");
      closeModal(true);
      fetchPolicies();
    } catch (error) {
      showMessage(error.response?.data?.message || "Policy upload failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDocument = async (policy) => {
    try {
      setViewingId(policy.id);
      const response = await axios.get(`${API_URL}/hr-policies/${policy.id}/view`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to open document", "error");
    } finally {
      setViewingId(null);
    }
  };

  const handleConfirmAction = async () => {
    if (!actionDialog.reason.trim()) {
      showMessage("Please enter reason", "error");
      return;
    }

    try {
      setSubmitting(true);

      if (actionDialog.type === "delete") {
        await axios.delete(`${API_URL}/hr-policies/${actionDialog.policy.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          data: { reason: actionDialog.reason.trim() },
        });
        showMessage("Policy deleted successfully");
      } else {
        await axios.post(
          `${API_URL}/hr-policies/${actionDialog.policy.id}/restore`,
          { reason: actionDialog.reason.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showMessage("Policy restored successfully");
      }

      setActionDialog(emptyAction);
      fetchPolicies();
    } catch (error) {
      showMessage(error.response?.data?.message || "Action failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const rows = useMemo(() => policies, [policies]);
  const showDeletedAudit =
    isTrashed || rows.some((policy) => policy.deleted_display_at || policy.deleted_by_name || policy.delete_reason);
  const showRestoredAudit = rows.some(
    (policy) => policy.restored_at || policy.restored_by_name || policy.restore_reason
  );

  const actionTitle = actionDialog.type === "delete" ? "Delete Policy" : "Restore Policy";
  const actionButtonText = actionDialog.type === "delete" ? "Delete" : "Confirm Restore";
  const actionColor = actionDialog.type === "delete" ? "error" : "success";

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="HR Policy"
        content={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="body2" sx={{ fontSize: "14px", fontWeight: 400 }}>
              {isTrashed ? "Trashed HR Policies" : "HR Policy Documents"}
            </SoftTypography>
            <SoftTypography variant="body2" color="text" sx={{ fontSize: "14px" }}>
              {isTrashed
                ? "Deleted policy documents with delete audit details."
                : "Uploaded HR policy letters and documents."}
            </SoftTypography>
          </SoftBox>

          <SoftBox display="flex" alignItems="center" gap={1}>
            {isTrashed ? (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>arrow_back</Icon>}
                onClick={() => navigate("/hr-letters/policy")}
                disabled={!canViewPolicy}
              >
                View Policy
              </SoftButton>
            ) : (
              <>
                {canTrashedPolicy && (
                  <SoftButton
                    size="small"
                    color="dark"
                    variant="outlined"
                    startIcon={<Icon>delete_sweep</Icon>}
                    onClick={() => navigate("/hr-letters/policy/trashed")}
                  >
                    View Trashed Policy
                  </SoftButton>
                )}
                {canAddPolicy && (
                  <SoftButton
                    size="small"
                    color="info"
                    startIcon={<Icon>add</Icon>}
                    onClick={() => setModalOpen(true)}
                  >
                    Add Policy
                  </SoftButton>
                )}
              </>
            )}
          </SoftBox>
        </SoftBox>

        <Card sx={{ overflow: "hidden", borderRadius: "18px", boxShadow: "0 14px 35px rgba(47, 69, 107, 0.08)" }}>
          {loading ? (
            <FlagLoader />
          ) : rows.length === 0 ? (
            <SoftBox py={6} textAlign="center">
              <SoftTypography variant="h6" color="text">
                {isTrashed ? "No trashed policy documents found." : "No policy documents uploaded yet."}
              </SoftTypography>
            </SoftBox>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
              <Table sx={{ minWidth: showDeletedAudit || showRestoredAudit ? 1280 : 950 }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Title</TableCell>
                    <TableCell sx={headerCellSx}>Description</TableCell>
                    <TableCell sx={headerCellSx}>Added By</TableCell>
                    <TableCell sx={headerCellSx}>Added At</TableCell>
                    <TableCell sx={headerCellSx}>Document</TableCell>
                    {showDeletedAudit && (
                      <>
                        <TableCell sx={headerCellSx}>Deleted At</TableCell>
                        <TableCell sx={headerCellSx}>Deleted By</TableCell>
                        <TableCell sx={headerCellSx}>Delete Reason</TableCell>
                      </>
                    )}
                    {showRestoredAudit && (
                      <>
                        <TableCell sx={headerCellSx}>Restored At</TableCell>
                        <TableCell sx={headerCellSx}>Restored By</TableCell>
                        <TableCell sx={headerCellSx}>Restore Reason</TableCell>
                      </>
                    )}
                    <TableCell sx={headerCellSx} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((policy) => (
                    <TableRow
                      key={policy.id}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "#f8fbff" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 220 }}>
                        <SoftTypography variant="body2" color="dark" sx={{ fontSize: "14px", fontWeight: 400 }}>
                          {policy.title}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 360 }}>
                        <SoftTypography variant="body2" color="text" sx={{ fontSize: "14px" }}>
                          {policy.description || "-"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <SoftTypography variant="body2" color="text" sx={{ fontSize: "14px" }}>
                          {policy.created_by_name || policy.created_by_email || "-"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <SoftTypography variant="body2" color="text" sx={{ fontSize: "14px" }}>
                          {formatDate(policy.created_at) || "-"}
                        </SoftTypography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <SoftTypography variant="body2" color="text" sx={{ fontSize: "14px" }}>
                          {policy.original_filename || "Document"}
                        </SoftTypography>
                        <SoftTypography variant="body2" color="secondary" display="block" sx={{ fontSize: "14px" }}>
                          {formatFileSize(policy.file_size)}
                        </SoftTypography>
                      </TableCell>

                      {showDeletedAudit && (
                        <>
                          <TableCell sx={bodyCellSx}>{formatDate(policy.deleted_display_at)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            {policy.deleted_by_name || policy.deleted_by_email || ""}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, maxWidth: 260 }}>{policy.delete_reason || ""}</TableCell>
                        </>
                      )}

                      {showRestoredAudit && (
                        <>
                          <TableCell sx={bodyCellSx}>{formatDate(policy.restored_at)}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            {policy.restored_by_name || policy.restored_by_email || ""}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, maxWidth: 260 }}>{policy.restore_reason || ""}</TableCell>
                        </>
                      )}

                      <TableCell sx={bodyCellSx} align="center">
                        {!isTrashed && canViewPolicy && (
                          <Tooltip title="View document">
                            <span>
                              <IconButton
                                color="info"
                                size="small"
                                sx={actionButtonSx}
                                onClick={() => handleViewDocument(policy)}
                                disabled={viewingId === policy.id}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {!isTrashed && canDeletePolicy && (
                          <Tooltip title="Delete policy">
                            <span>
                              <IconButton
                                color="error"
                                size="small"
                                sx={actionButtonSx}
                                onClick={() => openActionDialog("delete", policy)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {isTrashed && canRestorePolicy && (
                          <Tooltip title="Restore policy">
                            <span>
                              <IconButton
                                color="success"
                                size="small"
                                sx={actionButtonSx}
                                onClick={() => openActionDialog("restore", policy)}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </SoftBox>

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>Add Policy</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12}>
              <SoftTypography variant="body2" sx={{ fontSize: "14px", fontWeight: 400 }}>
                Title
              </SoftTypography>
              <SoftInput
                placeholder="Enter policy title"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <SoftTypography variant="body2" sx={{ fontSize: "14px", fontWeight: 400 }}>
                Description
              </SoftTypography>
              <SoftInput
                placeholder="Enter policy description"
                multiline
                minRows={4}
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <SoftTypography variant="body2" sx={{ fontSize: "14px", fontWeight: 400 }}>
                Document
              </SoftTypography>
              <SoftBox
                mt={1}
                p={2}
                borderRadius="lg"
                sx={{ border: "1px dashed #b8c2cc", backgroundColor: "#f8fafc" }}
              >
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, document: event.target.files?.[0] || null }))
                  }
                />
                <SoftTypography variant="caption" color="text" display="block" mt={1}>
                  Allowed: PDF, DOC, DOCX, JPG, PNG. Max size: 10 MB.
                </SoftTypography>
                {form.document && (
                  <SoftTypography variant="caption" color="info" display="block" mt={1}>
                    Selected: {form.document.name}
                  </SoftTypography>
                )}
              </SoftBox>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="dark" onClick={closeModal} disabled={submitting}>
            Cancel
          </SoftButton>
          <SoftButton color="info" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Uploading..." : "Upload Policy"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog.open} onClose={closeActionDialog} fullWidth maxWidth="sm">
        <DialogTitle>{actionTitle}</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text" mb={2}>
            {actionDialog.type === "delete"
              ? `Please enter delete reason for "${actionDialog.policy?.title || "this policy"}".`
              : `Please enter restore reason for "${actionDialog.policy?.title || "this policy"}".`}
          </SoftTypography>
          <SoftInput
            placeholder="Enter reason"
            multiline
            minRows={4}
            value={actionDialog.reason}
            onChange={(event) =>
              setActionDialog((prev) => ({ ...prev, reason: event.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="dark" onClick={closeActionDialog} disabled={submitting}>
            Cancel
          </SoftButton>
          <SoftButton color={actionColor} onClick={handleConfirmAction} disabled={submitting}>
            {submitting ? "Please wait..." : actionButtonText}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

HrPolicy.propTypes = {
  isTrashed: PropTypes.bool,
};

export default HrPolicy;
